import type { MapMember } from '@heartpatch/shared';
import { el } from '../ui/dom.js';
import { COMMUNITY_TEXT } from '../hollowed/community-model.js';
import {
  BORDER,
  PLAYER_COLORS,
  FALLBACK_LOOK,
  type BorderLine,
  type KeeperIcon,
} from './map-config.js';
import { drainRgb, hexRgb, muteRgb } from './map-dressing.js';

// The map's legend (#278): whose land is whose. The map's name pill shows
// each Keeper's icon; tapping it opens a card with every Keeper's colour,
// icon and line, so colour is never the only way to tell (design: owner
// decision 2026-10-08, direction D). It sits under the name and closes on
// any tap, so it never covers the chips below for long.

/** One Keeper in the legend, as the map draws their land. */
export interface LegendEntry {
  readonly slot: number;
  readonly name: string;
  readonly mine: boolean;
  readonly color: string;
  readonly icon: KeeperIcon;
  readonly line: BorderLine;
}

/** Every Keeper with a home on the map, in home-slot order (their colour's order). */
export function legendEntries(members: readonly MapMember[], userId: string | null): LegendEntry[] {
  return members
    .flatMap((m) => {
      const slot = m.homeSlot;
      if (slot === null) return [];
      return [
        {
          slot,
          name: m.user.username,
          mine: m.user.id === userId,
          color: PLAYER_COLORS[slot % PLAYER_COLORS.length] ?? '#ffffff',
          icon: BORDER.icons[slot % BORDER.icons.length] ?? 'heart',
          line: BORDER.lines[slot % BORDER.lines.length] ?? 'solid',
        },
      ];
    })
    .sort((a, b) => a.slot - b.slot);
}

/** What a legend row says (style guide: short and kid-readable). */
export function legendLabel(entry: LegendEntry): string {
  return entry.mine ? 'Your land' : `${entry.name}'s land`;
}

const SVG = 'http://www.w3.org/2000/svg';

/** `#rrggbb` from sRGB 0–1. */
const toHex = (c: readonly number[]): string =>
  `#${c
    .map((v) =>
      Math.round(Math.min(1, Math.max(0, v)) * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;

/**
 * The legend's swatch for a Hollowed village (#306): a meadow drawn as the
 * map draws a village tile (wild, then drained), so the two match.
 */
export function hollowedSwatch(): string {
  return toHex(drainRgb(muteRgb(hexRgb(FALLBACK_LOOK.color))));
}

/** The village row: a grey swatch with the doorstep's lavender rim. */
function villageRow(): HTMLElement {
  const svg = document.createElementNS(SVG, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '20');
  svg.setAttribute('height', '20');
  svg.setAttribute('aria-hidden', 'true');
  const hex = document.createElementNS(SVG, 'path');
  hex.setAttribute('d', 'M12 2.5l8.2 4.75v9.5L12 21.5l-8.2-4.75v-9.5z');
  hex.setAttribute('fill', hollowedSwatch());
  hex.setAttribute('stroke', BORDER.hollowed.color);
  hex.setAttribute('stroke-width', '2.5');
  hex.setAttribute('stroke-linejoin', 'round');
  svg.append(hex);
  return el(
    'li',
    { class: 'map-legend-row map-legend-village', 'data-testid': 'map-legend-village' },
    svg,
    el('span', { class: 'map-legend-name' }, COMMUNITY_TEXT.legend),
  );
}

/** Icon paths on a 24 × 24 grid, the same shapes as the badges on the land. */
const ICON_PATHS: Readonly<Record<KeeperIcon, string>> = {
  heart:
    'M12 21s-7.5-4.6-9.6-9C.9 8.6 3 5 6.6 5c2.1 0 3.5 1.2 5.4 3.2C13.9 6.2 15.3 5 17.4 5 21 5 23.1 8.6 21.6 12c-2.1 4.4-9.6 9-9.6 9z',
  star: 'M12 2.5l2.9 6.2 6.8.7-5.1 4.6 1.5 6.7L12 17.3 5.9 20.7l1.5-6.7L2.3 9.4l6.8-.7z',
  flower:
    'M12 2.5a3.5 3.5 0 013.3 4.6 3.5 3.5 0 014.3 5.2 3.5 3.5 0 01-2.6 6 3.5 3.5 0 01-5 2.2 3.5 3.5 0 01-5-2.2 3.5 3.5 0 01-2.6-6A3.5 3.5 0 018.7 7.1 3.5 3.5 0 0112 2.5z',
  diamond: 'M12 2l8 10-8 10-8-10z',
  // #318: homes 5 and 6.
  moon: 'M15 2.5a9.5 9.5 0 1 0 6.5 16A7.5 7.5 0 0 1 15 2.5z',
  leaf: 'M4 20C4 9 11 3 21 3c0 10-6 17-17 17z',
};

/** Each line's look as a short stroke: dashes, dots, or two thin lines. */
const LINE_STROKES: Readonly<
  Record<BorderLine, readonly { y: number; width: number; dash?: string }[]>
> = {
  solid: [{ y: 5, width: 5 }],
  dash: [{ y: 5, width: 5, dash: '5 8' }],
  dot: [{ y: 5, width: 5, dash: '0.1 7' }],
  double: [
    { y: 2.5, width: 2.5 },
    { y: 7.5, width: 2.5 },
  ],
  'dash-dot': [{ y: 5, width: 5, dash: '8 8 0.1 8' }],
  'long-dash': [{ y: 5, width: 5, dash: '14 9' }],
};

function icon(entry: LegendEntry, size: number): SVGSVGElement {
  const svg = document.createElementNS(SVG, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS(SVG, 'path');
  path.setAttribute('d', ICON_PATHS[entry.icon]);
  path.setAttribute('fill', entry.color);
  svg.append(path);
  return svg;
}

/**
 * A home slot's icon in its colour (#318), as on the map and in the legend:
 * for lists of Keepers (the lobby, a trading post's "Trade with"). Decorative;
 * the name next to it says who.
 */
export function slotIcon(slot: number, size = 18): SVGSVGElement {
  const mark = icon(
    {
      slot,
      name: '',
      mine: false,
      color: PLAYER_COLORS[slot % PLAYER_COLORS.length] ?? '#ffffff',
      icon: BORDER.icons[slot % BORDER.icons.length] ?? 'heart',
      line: BORDER.lines[slot % BORDER.lines.length] ?? 'solid',
    },
    size,
  );
  mark.classList.add('slot-icon');
  return mark;
}

function lineSample(entry: LegendEntry): SVGSVGElement {
  const svg = document.createElementNS(SVG, 'svg');
  svg.setAttribute('viewBox', '0 0 36 10');
  svg.setAttribute('width', '36');
  svg.setAttribute('height', '10');
  svg.setAttribute('aria-hidden', 'true');
  for (const stroke of LINE_STROKES[entry.line]) {
    const line = document.createElementNS(SVG, 'line');
    line.setAttribute('x1', '3');
    line.setAttribute('x2', '33');
    line.setAttribute('y1', String(stroke.y));
    line.setAttribute('y2', String(stroke.y));
    line.setAttribute('stroke', entry.color);
    line.setAttribute('stroke-width', String(stroke.width));
    line.setAttribute('stroke-linecap', 'round');
    if (stroke.dash) line.setAttribute('stroke-dasharray', stroke.dash);
    svg.append(line);
  }
  return svg;
}

export interface MapLegend {
  /** The Keepers' icons, for the name pill (decorative). */
  readonly icons: HTMLElement;
  /** The card that opens under the name. */
  readonly card: HTMLElement;
  /** Draws the legend for the map's members now, and the Hollowed villages' row when it has any (#306). */
  show: (members: readonly MapMember[], userId: string | null, villages?: boolean) => void;
  setOpen: (open: boolean) => void;
  readonly open: boolean;
}

export function mountMapLegend(): MapLegend {
  const icons = el('span', { class: 'map-legend-icons', 'aria-hidden': 'true' });
  const list = el('ul', { class: 'map-legend-list' });
  const card = el(
    'div',
    { class: 'map-legend', id: 'map-legend', 'data-testid': 'map-legend' },
    el('p', { class: 'map-legend-title' }, 'Whose land?'),
    list,
  );
  card.hidden = true;
  let shown = '';

  return {
    icons,
    card,
    show: (members, userId, villages = false) => {
      const entries = legendEntries(members, userId);
      const key = JSON.stringify([entries, villages]);
      if (key === shown) return;
      shown = key;
      icons.replaceChildren(...entries.map((e) => icon(e, 14)));
      list.replaceChildren(
        ...entries.map((e) =>
          el(
            'li',
            { class: `map-legend-row${e.mine ? ' map-legend-mine' : ''}` },
            icon(e, 20),
            el('span', { class: 'map-legend-name' }, legendLabel(e)),
            lineSample(e),
          ),
        ),
        ...(villages ? [villageRow()] : []),
      );
    },
    setOpen: (open) => {
      card.hidden = !open;
    },
    get open() {
      return !card.hidden;
    },
  };
}
